import { Component, ElementRef, HostListener, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { SharedService } from '../../../services/shared/shared.service';
import { RegistrationRequest } from '../../../models/registration-request';
import { AuthServiceService } from '../../../services/authService/auth-service.service';
import { AuthenticationRequest } from '../../../models/authentication-request';
import Swal from 'sweetalert2';
import { JobService } from '../../../services/job/job.service';
import { NotificationService } from '../../../services/notification/notification.service';
import { NotificationRequest } from '../../../models/notification-request';
import { UserDto } from '../../../models/user-dto';
import { FirestoreService } from '../../../services/firestore/firestore.service';
import { take } from 'rxjs';
import { SocialMediaAccount } from '../../../models/socialMediaAccount';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, FormsModule, CommonModule, CodeInputModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent {
  @ViewChild('codeInput1') codeInput1!: CodeInputComponent;
  @ViewChild('codeInput2') codeInput2!: CodeInputComponent;
  @ViewChild('mobileNavToggleBtn') mobileNavToggleBtn!: ElementRef<HTMLButtonElement>;

  forgotData = { email: '' };
  errorMessage: string | null = null;
  tokenCode = '';
  userEmail = '';
  tokenInfo: any;
  isLoggedIn = false;
  categories: string[] = [];
  notifications = new Array<NotificationRequest>();
  notificationCount = 0;
  userUid: any;
  userInfo: UserDto = {
    id: '',
    email: '',
    fullName: '',
  };
  adminIds: any[] = [];
  isAdmin = false;
  socialMediaAccounts: SocialMediaAccount[] = [];

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private spinner: NgxSpinnerService,
    private router: Router,
    public sharedService: SharedService,
    private authService: AuthServiceService,
    private jobService: JobService,
    private notificationService: NotificationService,
    private firestoreService: FirestoreService
  ) {}

  ngOnInit() {
    this.toggleActiveClass();
    this.loadSocialMediaAccounts();
    this.isUserLoggedIn();
    this.checkAdmin();
    this.getJobCategories();
    this.userUid = this.authService.getCurrentUserUid();
    this.getUserInfo();
    this.loadAdminIds();
  }

  checkAdmin() {
    this.authService.isAdmin().subscribe({
      next: (res) => {
        this.isAdmin = res;
      },
    });
  }

  ngAfterViewInit(): void {
    const navmenus = document.querySelectorAll('.navmenu .toggle-dropdown');

    navmenus.forEach(navmenu => {
      this.renderer.listen(navmenu, 'click', (event: Event) => {
        event.preventDefault();

        const parentNode = (navmenu as HTMLElement).parentNode as HTMLElement;
        const nextElement = parentNode?.nextElementSibling as HTMLElement;

        parentNode?.classList.toggle('active');
        nextElement?.classList.toggle('dropdown-active');

        event.stopImmediatePropagation();
      });
    });
  }

  loadSocialMediaAccounts() {
    this.firestoreService.getAll('socialMedia').subscribe({
      next: (accounts) => {
        this.socialMediaAccounts = accounts;
        if (this.socialMediaAccounts.length > 3) {
          this.socialMediaAccounts = this.socialMediaAccounts.slice(0, 3);
        }
      },
    });
  }

  mobileNavToggle() {
    document.body.classList.toggle('mobile-nav-active');
    const btn = this.mobileNavToggleBtn.nativeElement;
    btn.classList.toggle('bi-list');
    btn.classList.toggle('bi-x');
  }

  toggleActiveClass() {
    const navLinks = this.el.nativeElement.querySelectorAll('.navmenu a');
    navLinks.forEach((link: HTMLElement) => {
      this.renderer.listen(link, 'click', () => {
        navLinks.forEach((l: HTMLElement) => this.renderer.removeClass(l, 'active'));
        this.renderer.addClass(link, 'active');
      });
    });
  }

  login(form: NgForm) {
    this.spinner.show();
    if (form.invalid) {
      this.spinner.hide();
      return;
    }

    const request: AuthenticationRequest = {
      email: form.value["email"],
      password: form.value["password"]
    };

    this.authService.login(request).subscribe({
      next: res => {
        const user = res.user;

        if (!user.emailVerified) {
          this.spinner.hide();
          Swal.fire({
            icon: "warning",
            title: "E-Mail nicht verifiziert",
            text: "Bitte verifizieren Sie Ihre E-Mail-Adresse, bevor Sie sich anmelden.",
            confirmButtonText: "OK",
          });
          this.logOut();
          return;
        }

        this.isLoggedIn = true;
        this.userUid = this.authService.getCurrentUserUid();
        if (this.userUid) {
          const notif = new NotificationRequest();
          notif.title = "Anmeldung erfolgreich";
          notif.body = `Willkommen zurück, ${this.userInfo.fullName || 'Benutzer'}! Sie haben sich um ${new Date().toLocaleTimeString('de-DE')} angemeldet.`;
          notif.type = "LOGIN";
          notif.userIds = [this.userUid];
          notif.status = "UNREAD";

          this.notificationService.saveNotification(notif).subscribe({
            next: res => this.spinner.hide(),
            error: err => this.sharedService.handleError(err, undefined, "Fehler beim Senden der Anmeldebenachrichtigung.")
          });
        }

        this.getUserInfo();

        this.sharedService.handleSuccess("Erfolgreich angemeldet");

        this.authService.isAdmin().subscribe(isAdmin => {
          if (isAdmin) {
            this.router.navigateByUrl("/Admin/Dashboard");
          } else {
            this.router.navigateByUrl("/Profile");
          }
        });

        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err, "Anmeldung fehlgeschlagen");
        this.spinner.hide();
      }
    });
  }

  signup(form: NgForm) {
    this.spinner.show();

    const request: RegistrationRequest = {
      email: form.value["email"],
      fullName: form.value['fullName'],
      password: form.value["password"],
      role: 'USER'
    };

    this.authService.register(request).subscribe({
      next: res => {
        this.sharedService.handleSuccess("Registrierung erfolgreich", "Bitte verifizieren Sie Ihr Konto");
        console.log(res);

        this.firestoreService.getAdminUserIds().pipe(take(1)).subscribe({
          next: adminIds => {
            if (adminIds && adminIds.length > 0) {
              const notif = new NotificationRequest();
              notif.title = "Neuer Benutzer registriert";
              notif.body = `${form.value['fullName']} (${form.value['email']}) hat sich als neuer Benutzer registriert.`;
              notif.type = "NEW_USER_REGISTERED";
              notif.userIds = adminIds;
              notif.status = "UNREAD";

              this.notificationService.saveNotification(notif).subscribe({
                next: res => console.log('Admin-Benachrichtigung gesendet:', res),
                error: err => this.sharedService.handleError(err, undefined, "Fehler beim Senden der Admin-Benachrichtigung.")
              });
            }
          },
          error: err => this.sharedService.handleError(err, undefined, "Fehler beim Abrufen der Admin-IDs."),
          complete: () => this.spinner.hide()
        });
      },
      error: err => {
        this.sharedService.handleError(err, "Registrierung fehlgeschlagen");
        this.spinner.hide();
      }
    });
  }

  loginWithGoogle() {
    this.spinner.show();
    this.authService.loginWithGoogle().subscribe({
      next: (res) => {
        this.authService.handleSocialLogin(res.user).subscribe(() => {
          this.spinner.hide();
          this.sharedService.handleSuccess('Anmeldung mit Google erfolgreich');
          this.userUid = this.authService.getCurrentUserUid();
          if (this.userUid) {
            const notif = new NotificationRequest();
            notif.title = "Anmeldung erfolgreich";
            notif.body = `Willkommen zurück, ${this.userInfo.fullName || 'Benutzer'}! Sie haben sich um ${new Date().toLocaleTimeString('de-DE')} angemeldet.`;
            notif.type = "LOGIN";
            notif.userIds = [this.userUid];
            notif.status = "UNREAD";

            this.notificationService.saveNotification(notif).subscribe({});
            this.getUserInfo();

            this.authService.isAdmin().subscribe(isAdmin => {
              if (isAdmin) {
                this.router.navigateByUrl("/Admin/Dashboard");
              } else {
                this.router.navigateByUrl("/Profile");
              }
            });
          }
        });
      },
      error: (err) => {
        this.spinner.hide();
        this.sharedService.handleError(err, "Google-Anmeldung fehlgeschlagen");
      }
    });
  }

  loginWithFacebook() {
    this.spinner.show();
    this.authService.loginWithFacebook().subscribe({
      next: (res) => {
        this.authService.handleSocialLogin(res.user).subscribe(() => {
          this.sharedService.handleSuccess('Anmeldung mit Facebook erfolgreich');
          this.userUid = this.authService.getCurrentUserUid();
          if (this.userUid) {
            const notif = new NotificationRequest();
            notif.title = "Anmeldung erfolgreich";
            notif.body = `Willkommen zurück, ${this.userInfo.fullName || 'Benutzer'}! Sie haben sich um ${new Date().toLocaleTimeString('de-DE')} angemeldet.`;
            notif.type = "LOGIN";
            notif.userIds = [this.userUid];
            notif.status = "UNREAD";

            this.notificationService.saveNotification(notif).subscribe({});
            this.getUserInfo();

            this.authService.isAdmin().subscribe(isAdmin => {
              if (isAdmin) {
                this.router.navigateByUrl("/Admin/Dashboard");
              } else {
                this.router.navigateByUrl("/Profile");
              }
            });
          }
        });
      },
      error: (err) => {
        this.spinner.hide();
        this.sharedService.handleError(err, "Facebook-Anmeldung fehlgeschlagen");
      }
    });
  }

  restPassword(f: NgForm) {
    this.spinner.show();
    this.authService.restPassword(f.value["email"]).subscribe({
      next: () => {
        f.reset();
        this.sharedService.handleSuccess("E-Mail zum Zurücksetzen des Passworts gesendet. Überprüfen Sie Ihren Posteingang.");
      },
      error: err => this.sharedService.handleError(err),
      complete: () => this.spinner.hide()
    });
  }

  openModal(id: string) {
    this.errorMessage = null;
    this.tokenCode = '';
    if (this.codeInput1) {
      this.codeInput1.reset();
    }
    const modal = document.getElementById(id);
    if (modal) {
      const modalInstance = new (window as any).bootstrap.Modal(modal);
      modalInstance.show();
    }
  }

  closeModal(id: string) {
    const modalElement = document.getElementById(id);
    if (modalElement) {
      const modalInstance = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  userNavigate(token: any) {
    if (Array.isArray(token.role) && token.role.includes('ADMIN')) {
      this.router.navigate(['/Admin/Dashboard']);
    } 
    else if (Array.isArray(token.role) && token.role.includes('USER')) {
      this.router.navigate(['/user-profile']);
    }
    else {
      this.sharedService.handleError(null, 'Unbefugte Rolle');
      this.router.navigate(['/Home']);
    }
  }

  logOut() {
    this.authService.signOut().subscribe({
      next: res => {
        this.isLoggedIn = false;
        this.router.navigateByUrl("/Home");

        Swal.fire({
          icon: "info",
          title: "Abgemeldet",
          text: "Sie wurden erfolgreich abgemeldet.",
          confirmButtonText: "OK"
        });
      },
      error: err => {
        this.sharedService.handleError(err);
      }
    });
  }

  translateSite(lang: string) {
    const selectElement = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (selectElement) {
      selectElement.value = lang;
      selectElement.dispatchEvent(new Event('change'));
    }
  }

  isUserLoggedIn() {
    this.authService.isUserLoggedIn().subscribe({
      next: res => { 
        this.isLoggedIn = res;
        this.userUid = this.authService.getCurrentUserUid();
        this.getUserInfo();
      },
    });
  }

  getUserInfo() {
    this.firestoreService.getById("users", this.userUid).subscribe({
      next: res => {
        this.userInfo = res;
        this.getNewNotification(this.userUid);
        this.getNotifications(this.userUid);
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
      }
    });
  }

  getJobCategories(): void {
    this.jobService.getAllCategories().subscribe(cats => {
      this.categories = cats;
    });
  }

  getNotifications(userUid: string) {
    console.log("test");
    console.log("Benachrichtigungen abrufen für:", userUid);

    this.notificationService.getNotificationsByUserUid(userUid).subscribe({
      next: res => {
        this.notifications = res;
        console.log(this.notifications);
        if (this.notifications.length > 5) {
          this.notifications = this.notifications.slice(0, 5);
        }
      },
      error: err => console.log(err)
    });
  }

  getNewNotification(userUid: string) {
    this.notificationService.getNewNotificationCount(userUid).subscribe({
      next: res => this.notificationCount = res
    });
  }

  updateAllNotifications(userUid: string) {
    this.notificationService.updateNotificationsToNotNew(userUid).subscribe({
      next: () => {
        console.log('Alle neuen Benachrichtigungen auf "nicht neu" aktualisiert');
      },
      error: (err) => {
        console.error('Fehler beim Aktualisieren der Benachrichtigungen:', err);
      }
    });
  }

  private loadAdminIds() {
    this.firestoreService.getAdminUserIds().subscribe({
      next: adminIds => this.adminIds = adminIds,
      error: err => this.sharedService.handleError(err, undefined, "Fehler beim Laden der Admin-IDs.")
    });
  }

  onCategoryClick(event: Event, category: string): void {
    event.stopPropagation();
    this.mobileNavToggle();
  }
}